/**
 * BLRRD HDR Merge Modal — Lane C UI for the HDR fusion pipeline.
 *
 * openMerge(files) is the entry point (window.BlrrdHDR.openMerge).
 * Lazy-mounts a native <dialog>, decodes each bracket once, resolves
 * EV stops (EXIF first, fall back to estimateEVStops), then dispatches
 * fuseBracketsAuto with an AbortController for the cancel button.
 *
 * Strict rules: no innerHTML, no console.log, AbortSignal propagates,
 * ImageBitmap refs closed on cleanup. <800 lines per the brief.
 */

import { t } from '../i18n/index.js';

const MAX_BRACKETS  = 8;
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const THUMB_SIZE    = 96;

const state = {
    dialog: null, initialized: false,
    titleEl: null, bracketListEl: null, warningEl: null,
    backendBadgeEl: null, backendBadgeLabelEl: null,
    busyOverlayEl: null, busyTextEl: null, cancelBusyBtn: null,
    startBtn: null, cancelBtn: null, closeBtn: null, optionsRowEl: null,
    brackets: [],
    options: { levels: 5, contrast: 1, saturation: 1, exposure: 1, fusionMode: 'auto' },
    sourceSelectEl: null, fusionChipEl: null,
    activeAbort: null, busy: false,
    liveBitmaps: new Set(), nextBracketId: 1, evFallback: false,
};


function makeEl(tag, attrs = {}, ...children) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (v == null || v === false) continue;
        if (k === 'className') el.className = v;
        else if (k === 'dataset') Object.assign(el.dataset, v);
        else if (k.startsWith('on') && typeof v === 'function') {
            el.addEventListener(k.slice(2).toLowerCase(), v);
        } else if (k === 'aria') {
            for (const [ak, av] of Object.entries(v)) el.setAttribute(`aria-${ak}`, av);
        } else if (k in el && typeof el[k] !== 'object') {
            el[k] = v;
        } else {
            el.setAttribute(k, v);
        }
    }
    for (const c of children) {
        if (c == null || c === false) continue;
        if (typeof c === 'string') el.appendChild(document.createTextNode(c));
        else el.appendChild(c);
    }
    return el;
}

function clearChildren(el) {
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
}

function formatBytes(n) {
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatEV(ev) {
    if (!Number.isFinite(ev)) return '—';
    const sign = ev >= 0 ? '+' : '';
    return `${sign}${ev.toFixed(2)}`;
}

function safeToast(msg, kind) {
    try {
        if (typeof window !== 'undefined' && typeof window.toast === 'function') {
            window.toast(msg, kind || 'info');
        }
    } catch { /* tolerate missing toast */ }
}


function buildDialog() {
    state.closeBtn = makeEl('button', {
        className:    'hdr-dialog-close',
        type:         'button',
        'aria-label': t('library.close'),
        onClick:      closeDialog,
    }, '✕');

    state.titleEl = makeEl('h3', { id: 'hdrMergeTitle' }, t('hdr.merge.title'));

    const head = makeEl('div', { className: 'hdr-dialog-head' },
        state.titleEl,
        makeEl('div', { className: 'hdr-spacer' }),
        state.closeBtn,
    );

    state.bracketListEl = makeEl('div', { className: 'hdr-bracket-list' });

    state.warningEl = makeEl('div', { className: 'hdr-warning', hidden: true });

    state.optionsRowEl = makeEl('div', { className: 'hdr-options-row' },
        buildOptionSlider('levels',     3,   7,  1,    state.options.levels),
        buildOptionSlider('contrast',   0,   2,  0.1,  state.options.contrast),
        buildOptionSlider('saturation', 0,   2,  0.1,  state.options.saturation),
        buildOptionSlider('exposure',   0,   2,  0.1,  state.options.exposure),
    );

    /* Phase 2.A.2 — Source mode selector. Auto by default (Codex D2 UI
     * default = 'auto', API default = 'camera'). */
    state.sourceSelectEl = makeEl('select', {
        className: 'hdr-source-select',
        'aria-label': t('hdr.merge.source.label'),
        onChange: (e) => {
            state.options.fusionMode = e.target.value;
            /* Clear stale chip when user manually overrides. */
            if (state.fusionChipEl) state.fusionChipEl.hidden = true;
        },
    });
    for (const [val, key] of [['auto', 'auto'], ['camera', 'camera'], ['robust', 'robust']]) {
        const opt = makeEl('option', { value: val }, t('hdr.merge.source.' + key));
        if (val === state.options.fusionMode) opt.selected = true;
        state.sourceSelectEl.appendChild(opt);
    }
    state.fusionChipEl = makeEl('span', {
        className: 'hdr-fusion-chip',
        hidden: true,
    });
    const sourceRow = makeEl('div', { className: 'hdr-source-row' },
        makeEl('label', { className: 'hdr-source-label' }, t('hdr.merge.source.label') + ': '),
        state.sourceSelectEl,
        state.fusionChipEl,
    );

    state.backendBadgeLabelEl = makeEl('span', { className: 'hdr-backend-label' },
        t('hdr.merge.backend') + ': ');
    state.backendBadgeEl = makeEl('span', { className: 'hdr-backend-badge' });
    state.backendBadgeEl.setAttribute('data-hdr-backend-badge', '');

    const backendRow = makeEl('div', { className: 'hdr-backend-row' },
        state.backendBadgeLabelEl,
        state.backendBadgeEl,
    );

    state.cancelBtn = makeEl('button', {
        className: 'hdr-btn hdr-btn-cancel',
        type:      'button',
        onClick:   closeDialog,
    }, t('hdr.merge.cancel'));

    state.startBtn = makeEl('button', {
        className: 'hdr-btn hdr-btn-primary',
        type:      'button',
        onClick:   handleStart,
    }, t('hdr.merge.start'));
    state.startBtn.setAttribute('data-hdr-merge-button', '');

    const foot = makeEl('div', { className: 'hdr-dialog-foot' },
        state.cancelBtn,
        makeEl('div', { className: 'hdr-spacer' }),
        state.startBtn,
    );

    state.busyTextEl = makeEl('div', { className: 'hdr-busy-text' });
    state.cancelBusyBtn = makeEl('button', {
        className: 'hdr-btn hdr-btn-cancel',
        type:      'button',
        onClick:   handleAbortBusy,
    }, t('hdr.merge.cancel'));
    state.busyOverlayEl = makeEl('div', { className: 'hdr-busy-overlay', hidden: true },
        makeEl('div', { className: 'hdr-busy-inner' },
            state.busyTextEl,
            state.cancelBusyBtn,
        ),
    );

    const body = makeEl('div', { className: 'hdr-dialog-body' },
        state.bracketListEl,
        state.warningEl,
        state.optionsRowEl,
        sourceRow,
        backendRow,
        state.busyOverlayEl,
    );

    const dialog = makeEl('dialog', {
        className:        'hdr-merge-dialog',
        'aria-labelledby':'hdrMergeTitle',
    }, head, body, foot);
    dialog.setAttribute('data-hdr-merge-dialog', '');

    /* Close on backdrop click — same semantics as the Library modal. */
    dialog.addEventListener('click', (e) => {
        if (e.target === dialog && !state.busy) closeDialog();
    });

    /* Cleanup on close (Esc / × / programmatic). */
    dialog.addEventListener('close', () => {
        cleanupBrackets();
    });

    /* Cancel-key needs special treatment while merging — Esc must NOT
     * silently close mid-fusion; route through abort instead. */
    dialog.addEventListener('cancel', (e) => {
        if (state.busy) {
            e.preventDefault();
            handleAbortBusy();
        }
    });

    document.body.appendChild(dialog);

    state.dialog      = dialog;
    state.initialized = true;
}

function buildOptionSlider(name, min, max, step, defaultValue) {
    const valueEl = makeEl('div', { className: 'hdr-option-val' }, formatOptionValue(name, defaultValue));
    const slider = makeEl('input', {
        type:      'range',
        className: 'hdr-option-slider',
        min:       String(min),
        max:       String(max),
        step:      String(step),
        value:     String(defaultValue),
    });
    slider.dataset.option = name;
    slider.addEventListener('input', () => {
        const val = name === 'levels'
            ? Math.round(Number(slider.value))
            : Number(slider.value);
        state.options[name] = val;
        valueEl.textContent = formatOptionValue(name, val);
    });

    return makeEl('div', { className: 'hdr-option' },
        makeEl('label', { className: 'hdr-option-label' }, t('hdr.merge.' + name)),
        slider,
        valueEl,
    );
}

function formatOptionValue(name, val) {
    if (name === 'levels') return String(val | 0);
    return Number(val).toFixed(2);
}


export async function openMerge(files) {
    if (!state.initialized) buildDialog();

    /* Codex HDR-C review fix (CRITICAL): re-entrant openMerge while
     * handleStart() is still running races on shared modal state. The
     * in-flight merge keeps reading state.brackets / writing the editor
     * via injectFusedBracket(); a second openMerge() would clear
     * state.brackets, decode new ones, and we'd potentially inject the
     * WRONG result. Two safe options:
     *   (a) hard-bail with a toast while busy; user retries after merge
     *       settles or cancels.
     *   (b) abort + drain.
     * (a) is simplest and correct — refuse to re-enter. The cancel
     * button on the busy overlay is the supported way to interrupt. */
    if (state.busy) {
        safeToast(t('hdr.merge.busy'), 'info');
        if (state.dialog && !state.dialog.open) {
            try { state.dialog.showModal(); } catch { /* already open */ }
        }
        return;
    }

    cleanupBrackets();
    state.evFallback = false;
    refreshStaticLabels();
    refreshBackendBadge();
    if (!state.dialog.open) {
        try { state.dialog.showModal(); } catch { /* already open */ }
    }

    const list = Array.isArray(files) ? files : Array.from(files || []);
    let usable = list.filter(Boolean);
    if (usable.length > MAX_BRACKETS) {
        safeToast(t('hdr.merge.tooManyFiles'), 'info');
        usable = usable.slice(0, MAX_BRACKETS);
    }

    const accepted = [];
    for (const f of usable) {
        if (!isImageBlob(f)) {
            safeToast(t('hdr.merge.notImage', { name: blobLabel(f) }), 'err');
            continue;
        }
        if (f.size > MAX_FILE_SIZE) {
            safeToast(t('hdr.merge.fileTooLarge', {
                name: blobLabel(f), size: formatBytes(f.size),
            }), 'err');
            continue;
        }
        accepted.push(f);
    }

    if (accepted.length === 0) { renderEmpty(); return; }

    for (const blob of accepted) {
        if (!state.dialog.open) break;
        await ingestBracket(blob);
        if (!state.dialog.open) break;
    }

    await resolveEVs();
    refreshBracketCards();
    refreshState();
}


async function ingestBracket(blob) {
    let bitmap = null;
    try {
        bitmap = await createImageBitmap(blob, BITMAP_OPTS);
    } catch {
        safeToast(t('hdr.merge.notImage', { name: blob.name || 'image' }), 'err');
        return;
    }
    if (!bitmap || !bitmap.width || !bitmap.height) {
        try { bitmap?.close?.(); } catch { /* ignore */ }
        return;
    }
    state.liveBitmaps.add(bitmap);

    const item = {
        id: state.nextBracketId++, blob,
        name: blob.name || 'image', size: blob.size || 0,
        type: blob.type || 'image/jpeg',
        width: bitmap.width, height: bitmap.height,
        bitmap, thumbCanvas: drawThumbnail(bitmap, THUMB_SIZE),
        ev: null, evEstimated: false, cardEl: null, evBadgeEl: null,
    };
    state.brackets.push(item);
    appendBracketCard(item);
    refreshState();
}

const BITMAP_OPTS = {
    imageOrientation:     'from-image',
    premultiplyAlpha:     'none',
    colorSpaceConversion: 'none',
};

function drawThumbnail(bitmap, size) {
    const c = document.createElement('canvas');
    c.width  = size;
    c.height = size;
    const ctx = c.getContext('2d');
    if (!ctx) return c;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    /* Cover-fit: scale so the shorter side fills the box, then center. */
    const ar  = bitmap.width / bitmap.height;
    let dw, dh, dx, dy;
    if (ar >= 1) {
        dh = size; dw = Math.round(size * ar);
        dx = Math.round((size - dw) / 2);
        dy = 0;
    } else {
        dw = size; dh = Math.round(size / ar);
        dx = 0;
        dy = Math.round((size - dh) / 2);
    }
    ctx.fillStyle = '#08080C';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(bitmap, dx, dy, dw, dh);
    return c;
}

function appendBracketCard(item) {
    const thumbWrap = makeEl('div', { className: 'hdr-bracket-thumb' });
    if (item.thumbCanvas) thumbWrap.appendChild(item.thumbCanvas);

    item.evBadgeEl = makeEl('span', { className: 'hdr-bracket-ev' }, '…');

    const meta = makeEl('div', { className: 'hdr-bracket-meta' },
        makeEl('div', { className: 'hdr-bracket-name', title: item.name }, item.name),
        makeEl('div', { className: 'hdr-bracket-row' },
            item.evBadgeEl,
            makeEl('span', { className: 'hdr-bracket-dim' },
                `${item.width} × ${item.height}`),
        ),
    );

    const removeBtn = makeEl('button', {
        className:    'hdr-bracket-remove',
        type:         'button',
        'aria-label': t('hdr.merge.remove'),
        title:        t('hdr.merge.remove'),
        onClick:      () => removeBracket(item.id),
    }, '✕');

    const card = makeEl('div', { className: 'hdr-bracket-card' },
        thumbWrap, meta, removeBtn);
    card.dataset.bracketId = String(item.id);
    item.cardEl = card;

    state.bracketListEl.appendChild(card);
}

function removeBracket(id) {
    const idx = state.brackets.findIndex((b) => b.id === id);
    if (idx < 0) return;
    const [item] = state.brackets.splice(idx, 1);
    if (item) {
        if (item.bitmap) {
            try { item.bitmap.close?.(); } catch { /* ignore */ }
            state.liveBitmaps.delete(item.bitmap);
            item.bitmap = null;
        }
        if (item.cardEl && item.cardEl.parentNode) {
            item.cardEl.parentNode.removeChild(item.cardEl);
        }
    }
    /* EV stops depend on the stack — refresh badges on removal. */
    resolveEVs().then(() => {
        refreshBracketCards();
        refreshState();
    });
    refreshState();
}


async function resolveEVs() {
    state.evFallback = false;
    if (!window.BlrrdHDR || state.brackets.length === 0) return;

    /* All-or-nothing EXIF — fall back to pixel estimate on any miss. */
    const exifEVs = [];
    for (const b of state.brackets) {
        let abs = null;
        try { abs = await window.BlrrdHDR.parseExifEV(b.blob); } catch { abs = null; }
        if (abs == null || !Number.isFinite(abs)) { exifEVs.length = 0; break; }
        exifEVs.push(abs);
    }
    if (exifEVs.length === state.brackets.length) {
        const sorted = exifEVs.slice().sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        for (let i = 0; i < state.brackets.length; i++) {
            state.brackets[i].ev = exifEVs[i] - median;
            state.brackets[i].evEstimated = false;
        }
        return;
    }

    state.evFallback = true;
    const stack = [];
    for (const b of state.brackets) {
        try {
            const rgba = await decodeBracketToFloat32(b);
            if (rgba) stack.push({ rgba, width: b.width, height: b.height });
        } catch { /* ignore */ }
    }
    let evStops = null;
    if (stack.length === state.brackets.length) {
        try { evStops = window.BlrrdHDR.estimateEVStops(stack); } catch { evStops = null; }
    }
    for (let i = 0; i < state.brackets.length; i++) {
        const v = evStops && evStops[i];
        state.brackets[i].ev = Number.isFinite(v) ? v : 0;
        state.brackets[i].evEstimated = true;
    }
}

async function decodeBracketToFloat32(item) {
    if (typeof window.BlrrdPipeline?.ingestSRGB !== 'function') return null;
    let bitmap = item.bitmap;
    let temporary = false;
    if (!bitmap) {
        try {
            bitmap = await createImageBitmap(item.blob, BITMAP_OPTS);
            temporary = true;
        } catch { return null; }
    }
    try {
        const ctx = makeOffscreenCtx(bitmap.width, bitmap.height);
        if (!ctx) return null;
        ctx.drawImage(bitmap, 0, 0);
        const imgData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
        return window.BlrrdPipeline.ingestSRGB(imgData.data, bitmap.width, bitmap.height);
    } catch { return null; }
    finally {
        if (temporary) { try { bitmap.close?.(); } catch { /* ignore */ } }
    }
}

function makeOffscreenCtx(w, h) {
    if (typeof OffscreenCanvas !== 'undefined') {
        const c = new OffscreenCanvas(w, h);
        const ctx = c.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' });
        if (ctx) return ctx;
    }
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' });
}


function refreshStaticLabels() {
    if (!state.dialog) return;
    if (state.titleEl)   state.titleEl.textContent   = t('hdr.merge.title');
    if (state.cancelBtn) state.cancelBtn.textContent = t('hdr.merge.cancel');
    if (state.startBtn)  state.startBtn.textContent  = t('hdr.merge.start');
    if (state.cancelBusyBtn) state.cancelBusyBtn.textContent = t('hdr.merge.cancel');
    if (state.backendBadgeLabelEl) {
        state.backendBadgeLabelEl.textContent = t('hdr.merge.backend') + ': ';
    }
    if (state.closeBtn) state.closeBtn.setAttribute('aria-label', t('library.close'));
    /* Option labels */
    if (state.optionsRowEl) {
        state.optionsRowEl.querySelectorAll('.hdr-option-slider').forEach((sl) => {
            const opt = sl.dataset.option;
            const labelEl = sl.previousElementSibling;
            if (labelEl && opt) labelEl.textContent = t('hdr.merge.' + opt);
        });
    }
}

function refreshBackendBadge() {
    let backend = 'cpu';
    try {
        const sel = window.BlrrdHDR?.selectBackend?.();
        if (sel?.backend) backend = sel.backend;
    } catch { backend = 'cpu'; }
    paintBackendBadge(backend);
}

function refreshBracketCards() {
    /* Re-render EV badges, plus inject the estimated footnote. */
    for (const b of state.brackets) {
        if (!b.evBadgeEl) continue;
        const ev = formatEV(b.ev);
        b.evBadgeEl.textContent = b.evEstimated
            ? t('hdr.merge.evEstimated', { ev })
            : t('hdr.merge.evBadge',     { ev });
    }
    /* Inject / remove the footnote row. */
    const existing = state.dialog.querySelector('.hdr-exif-footnote');
    if (state.evFallback && state.brackets.length > 0) {
        if (!existing) {
            const note = makeEl('div', { className: 'hdr-exif-footnote' },
                t('hdr.merge.exifMissingNote'));
            state.bracketListEl.parentNode.insertBefore(note, state.warningEl);
        } else {
            existing.textContent = t('hdr.merge.exifMissingNote');
        }
    } else if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
    }
}

function renderEmpty() {
    clearChildren(state.bracketListEl);
    const msg = makeEl('div', { className: 'hdr-bracket-empty' },
        t('hdr.merge.empty'));
    state.bracketListEl.appendChild(msg);
    refreshState();
}

function refreshState() {
    if (!state.dialog) return;

    /* Empty state when no cards. */
    const hasEmptyMsg = !!state.bracketListEl.querySelector('.hdr-bracket-empty');
    if (state.brackets.length === 0 && !hasEmptyMsg) {
        renderEmpty();
        return;
    }
    if (state.brackets.length > 0 && hasEmptyMsg) {
        const msg = state.bracketListEl.querySelector('.hdr-bracket-empty');
        if (msg && msg.parentNode) msg.parentNode.removeChild(msg);
    }

    /* Dimension mismatch detection. */
    let mismatch = false;
    if (state.brackets.length >= 2) {
        const w = state.brackets[0].width;
        const h = state.brackets[0].height;
        for (let i = 1; i < state.brackets.length; i++) {
            if (state.brackets[i].width !== w || state.brackets[i].height !== h) {
                mismatch = true;
                break;
            }
        }
    }

    /* Warning row content. */
    if (state.brackets.length < 2) {
        state.warningEl.hidden = false;
        state.warningEl.textContent = t('hdr.merge.needTwo');
    } else if (mismatch) {
        state.warningEl.hidden = false;
        state.warningEl.textContent = t('hdr.merge.dimensionsMismatch');
    } else {
        state.warningEl.hidden = true;
        state.warningEl.textContent = '';
    }

    /* Start button enable state. */
    const canStart = state.brackets.length >= 2 && !mismatch && !state.busy;
    state.startBtn.disabled = !canStart;
}


async function handleStart() {
    if (state.busy || state.brackets.length < 2) return;

    /* Drain autosave first — same FB-UI pattern as Library wipe. */
    if (typeof window.cancelPendingAutosave === 'function') {
        window.cancelPendingAutosave();
    }
    if (typeof window.waitForAutosaveIdle === 'function') {
        let idle = false;
        try { idle = await window.waitForAutosaveIdle(); } catch { idle = false; }
        if (!idle) { window.alert(t('library.busy')); return; }
    }

    setBusy(true, t('hdr.merge.busy'));
    state.activeAbort = new AbortController();
    const signal = state.activeAbort.signal;

    try {
        const stack = [];
        for (let i = 0; i < state.brackets.length; i++) {
            checkAbort(signal);
            setBusy(true, t('hdr.merge.decoding', {
                i: String(i + 1), n: String(state.brackets.length),
            }));
            const b = state.brackets[i];
            const rgba = await decodeBracketToFloat32(b);
            if (!rgba) throw new Error(`bracket ${b.name} decode failed`);
            stack.push({ rgba, width: b.width, height: b.height });
        }
        checkAbort(signal);

        const evResult = await computeEvStopsForFusion(stack, signal);
        const evStops = evResult.evStops;
        const evExifMissing = evResult.exifMissing;
        checkAbort(signal);

        /* Apply 2^(-EV) scale to a CLONE — fuseBracketsAuto may mutate. */
        const scaled = stack.map((entry, i) => {
            const ev = Number.isFinite(evStops[i]) ? evStops[i] : 0;
            const factor = Math.pow(2, -ev);
            const cloned = new Float32Array(entry.rgba);
            if (factor !== 1) {
                for (let k = 0; k < cloned.length; k += 4) {
                    cloned[k]     *= factor;
                    cloned[k + 1] *= factor;
                    cloned[k + 2] *= factor;
                }
            }
            return { rgba: cloned, width: entry.width, height: entry.height };
        });
        for (let i = 0; i < stack.length; i++) stack[i].rgba = null;

        setBusy(true, t('hdr.merge.fusing'));
        checkAbort(signal);

        if (typeof window.BlrrdHDR?.fuseBracketsAuto !== 'function') {
            throw new Error('fuseBracketsAuto unavailable');
        }
        /* Phase 2.A.2 (Codex HIGH-1): UI path bypasses merge.js, so the
         * camera/robust fusionMode must be resolved HERE before calling
         * the backend. detectFusionMode lives on window.BlrrdHDR.
         *
         * Codex 2.A.2.fix HIGH-1: pass explicit exifMissing flag —
         * computeEvStopsForFusion fills evStops with pixel-median
         * estimates when EXIF is absent, so the auto-detect helper can't
         * infer the missing-EXIF signal from finite evStops alone.
         * state.evFallback is the authoritative source. */
        let resolvedMode = state.options.fusionMode;
        let detection = null;
        if (resolvedMode === 'auto'
                && typeof window.BlrrdHDR.detectFusionMode === 'function') {
            try {
                detection = window.BlrrdHDR.detectFusionMode(scaled, {
                    evStops,
                    /* Codex 2.A.2.fix R3 STOP-1: use the merge-time
                     * EXIF probe result (computeEvStopsForFusion above),
                     * NOT the preview-time state.evFallback which can be
                     * stale if the bracket list changed since preview. */
                    exifMissing: !!evExifMissing,
                });
                resolvedMode = detection.mode;
            } catch {
                resolvedMode = 'camera';   /* fail safe */
            }
        } else if (resolvedMode !== 'robust' && resolvedMode !== 'camera') {
            resolvedMode = 'camera';
        }
        const result = await window.BlrrdHDR.fuseBracketsAuto(scaled, {
            levels:     state.options.levels,
            contrast:   state.options.contrast,
            saturation: state.options.saturation,
            exposure:   state.options.exposure,
            fusionMode: resolvedMode,
            signal,
        });
        /* Auto-detected switch → non-blocking toast + override chip. */
        if (state.options.fusionMode === 'auto' && detection) {
            if (detection.mode === 'robust') {
                safeToast(t('hdr.merge.autoDetectRobust'), 'info');
                if (state.fusionChipEl) {
                    state.fusionChipEl.textContent = t('hdr.merge.fusionChip.robust');
                    state.fusionChipEl.hidden = false;
                }
            } else if (state.fusionChipEl) {
                state.fusionChipEl.hidden = true;
            }
        }
        if (!result || !(result.rgba instanceof Float32Array)) {
            throw new Error('fuseBracketsAuto returned no buffer');
        }
        if (result.backend) paintBackendBadge(result.backend);
        checkAbort(signal);

        const fileName = (state.brackets[0]?.name || 'image') + ' (HDR)';
        if (typeof window.injectFusedBracket === 'function') {
            window.injectFusedBracket(result, {
                fileName, evStops, sourceBrackets: scaled.length,
            });
        }
        /* Codex R10 fix: drop busy BEFORE closeDialog so it actually
         * closes + cleans up. closeDialog() routes to handleAbortBusy()
         * while busy=true, which would leave the success-path dialog
         * open with stale brackets/liveBitmaps. The finally block calls
         * setBusy(false) again — idempotent. */
        setBusy(false);
        closeDialog();
    } catch (err) {
        if (err && err.name === 'AbortError') {
            safeToast(t('hdr.merge.cancelled'), 'info');
        } else {
            const msg = err && err.message ? err.message : String(err || 'unknown');
            safeToast(t('hdr.merge.error', { message: msg }), 'err');
        }
    } finally {
        setBusy(false);
        state.activeAbort = null;
        refreshState();
    }
}

/**
 * Returns { evStops, exifMissing } so the caller can pass an authoritative
 * exifMissing flag to detectFusionMode at MERGE time. Codex 2.A.2.fix R3
 * STOP-1: state.evFallback is set at preview time and may be stale by the
 * time handleStart runs (e.g. user removed/added brackets after preview).
 * Re-probing here keeps detection accurate. */
async function computeEvStopsForFusion(stack, signal) {
    const hdr = window.BlrrdHDR;
    if (!hdr) return { evStops: new Float32Array(stack.length), exifMissing: true };
    const abs = new Array(state.brackets.length);
    let exifOk = true;
    for (let i = 0; i < state.brackets.length; i++) {
        checkAbort(signal);
        let v = null;
        try { v = await hdr.parseExifEV(state.brackets[i].blob); } catch { v = null; }
        if (v == null || !Number.isFinite(v)) { exifOk = false; break; }
        abs[i] = v;
    }
    if (exifOk) {
        const sorted = abs.slice().sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        const out = new Float32Array(state.brackets.length);
        for (let i = 0; i < state.brackets.length; i++) out[i] = abs[i] - median;
        return { evStops: out, exifMissing: false };
    }
    const fallback = typeof hdr.estimateEVStops === 'function'
        ? hdr.estimateEVStops(stack)
        : new Float32Array(stack.length);
    return { evStops: fallback, exifMissing: true };
}

function paintBackendBadge(backend) {
    if (!state.backendBadgeEl) return;
    let label, tier;
    if (backend === 'webgl2-32f')      { label = t('hdr.merge.backend32f'); tier = 'tier-32f'; }
    else if (backend === 'webgl2-16f') { label = t('hdr.merge.backend16f'); tier = 'tier-16f'; }
    else                                { label = t('hdr.merge.backendCpu'); tier = 'tier-cpu'; }
    state.backendBadgeEl.textContent = label;
    state.backendBadgeEl.classList.remove('tier-32f', 'tier-16f', 'tier-cpu');
    state.backendBadgeEl.classList.add(tier);
}

function checkAbort(signal) {
    if (signal && signal.aborted) {
        const err = new Error('cancelled');
        err.name = 'AbortError';
        throw err;
    }
}

function handleAbortBusy() {
    if (state.activeAbort) {
        try { state.activeAbort.abort(); } catch { /* ignore */ }
    }
}

function setBusy(busy, message) {
    state.busy = !!busy;
    if (state.busyOverlayEl) state.busyOverlayEl.hidden = !busy;
    if (state.busyTextEl)    state.busyTextEl.textContent = busy ? (message || '') : '';
    if (state.startBtn)      state.startBtn.disabled = !!busy;
    if (state.cancelBtn)     state.cancelBtn.disabled = !!busy;
    /* Disable card removal while busy — abort first. */
    state.dialog?.querySelectorAll('.hdr-bracket-remove').forEach((b) => {
        b.disabled = !!busy;
    });
}


function isImageBlob(b) {
    if (!b || typeof b !== 'object') return false;
    if (typeof b.size !== 'number') return false;
    const type = (b.type || '').toLowerCase();
    return type.startsWith('image/');
}

function blobLabel(b) {
    return (b && (b.name || b.type)) || 'file';
}

function cleanupBrackets() {
    /* Close every live bitmap, drop card refs, clear list. */
    for (const b of state.brackets) {
        if (b && b.bitmap) {
            try { b.bitmap.close?.(); } catch { /* ignore */ }
            state.liveBitmaps.delete(b.bitmap);
        }
    }
    state.liveBitmaps.forEach((bm) => {
        try { bm.close?.(); } catch { /* ignore */ }
    });
    state.liveBitmaps.clear();
    state.brackets = [];
    if (state.bracketListEl) clearChildren(state.bracketListEl);
    if (state.warningEl) {
        state.warningEl.hidden = true;
        state.warningEl.textContent = '';
    }
    /* Remove the footnote line if present. */
    const footnote = state.dialog?.querySelector('.hdr-exif-footnote');
    if (footnote && footnote.parentNode) footnote.parentNode.removeChild(footnote);
    state.evFallback = false;
    /* Codex 2.A.2.fix LOW-2: clear robust-mode chip so it doesn't leak
     * across modal sessions. The chip is only meaningful for the
     * just-completed run; reopening the dialog must show a clean slate. */
    if (state.fusionChipEl) {
        state.fusionChipEl.hidden = true;
        state.fusionChipEl.textContent = '';
    }
}

function closeDialog() {
    /* Close-while-busy is illegal; we route through abort first. */
    if (state.busy) {
        handleAbortBusy();
        return;
    }
    if (state.dialog && state.dialog.open) {
        try { state.dialog.close(); } catch { /* ignore */ }
    }
    cleanupBrackets();
}
